/**
 * ReferAdvisorModal Component
 * Allows advisors to refer other financial advisors to the platform
 */
import React, { useState } from 'react';
import { FaTimes, FaUserTie, FaSpinner, FaPhone, FaEnvelope, FaUser, FaBriefcase } from 'react-icons/fa';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferAdvisorModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    profile: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/advisor/refer-advisor`, formData, { headers: headers() });
      setFormData({ name: '', contact: '', email: '', profile: '' });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit referral. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1A2942] rounded-xl border border-[#2D5A8F] max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-[#2D5A8F]">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FFD700] to-[#FFA500] flex items-center justify-center">
                <FaUserTie size={20} className="text-[#0A1929]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Refer an Advisor</h2>
                <p className="text-gray-400 text-sm">Earn Rs 1500/year per successful referral</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-[#2D5A8F] rounded-lg transition-all"
            >
              <FaTimes size={20} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Reward Info */}
          <div className="bg-gradient-to-r from-[#FFD700]/10 to-[#FFA500]/10 border border-[#FFD700]/30 rounded-lg p-4">
            <p className="text-[#FFD700] text-sm font-medium">
              Earn Rs 1,500 per year for each advisor you refer who successfully registers and onboards on Arth Gurukul!
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              <FaUser className="inline mr-2" />
              Advisor Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter advisor's full name"
              required
              className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] focus:outline-none"
              data-testid="refer-advisor-name"
            />
          </div>

          {/* Contact */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              <FaPhone className="inline mr-2" />
              Contact Number *
            </label>
            <input
              type="tel"
              value={formData.contact}
              onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
              placeholder="Enter 10-digit mobile number"
              required
              pattern="[0-9]{10}"
              className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] focus:outline-none"
              data-testid="refer-advisor-contact"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              <FaEnvelope className="inline mr-2" />
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Enter advisor's email"
              required
              className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] focus:outline-none"
              data-testid="refer-advisor-email"
            />
          </div>

          {/* Profile */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              <FaBriefcase className="inline mr-2" />
              Profile / Background
            </label>
            <textarea
              value={formData.profile}
              onChange={(e) => setFormData({ ...formData, profile: e.target.value })}
              placeholder="Brief description about the advisor (experience, specialization, etc.)"
              rows={3}
              className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] focus:outline-none resize-none"
              data-testid="refer-advisor-profile"
            />
          </div>

          {/* Actions */}
          <div className="flex space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
              data-testid="submit-advisor-referral-btn"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <span>Submit Referral</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReferAdvisorModal;
